import { useSelector } from "react-redux"
import moment from "moment-timezone"

/**
 * Returns the current time in minutes since midnight
 * @returns {number}
 */
export function getCurrentTime(isSandboxMode, isTomorrowMode) {

	if (isSandboxMode === undefined) {
		throw new Error("isSandboxMode is undefined")
	}

	if (isTomorrowMode === undefined) {
		throw new Error("isTomorrowMode is undefined")
	}

	let currentTime = 0
	if (isSandboxMode || isTomorrowMode) {
        currentTime = 420
    } else {
        const now = new Date()
        currentTime = now.getHours() * 60 + now.getMinutes()
    }
    return currentTime
}

/**
 * Returns the current hour
 * @returns {number}
 */
export function getCurrentHours() {
	const now = new Date()
	return now.getHours()
}

export const getDateToday = test_environment => {
	const now = moment.tz(new Date(), "America/Los_Angeles")
	return now.toDate()
}

export const getDateTomorrow = test_environment => {
	const now = getDateToday()
	now.setDate(now.getDate() + 1) // Move to the next day
	return now
}

export const getDateForString = dateString => {
	const date = new Date(dateString + " GMT-0800")
	return date
}

/**
 * Checks if a given date is a holiday based on holiday definitions from settings
 * @param {Date} date - The date to check
 * @param {Array} centerClosedDates - Array of holiday definitions from backend settings
 * @returns {boolean} - True if the date is a holiday, false otherwise
 */
const isHoliday = (date, centerClosedDates = []) => {
	const month = date.getMonth() + 1; // Convert JS 0-based to 1-based to match Python
	const day = date.getDate();
	// Convert JS weekdays (Sun=0) to Python weekdays (Mon=0): (jsDay + 6) % 7
	const dayOfWeek = (date.getDay() + 6) % 7; // JS: Sun=0,Mon=1 → Python: Mon=0,Tue=1

	for (const holiday of centerClosedDates) {
		if (holiday.type === "fixed_date") {
			if (month === holiday.month && day === holiday.day) {
				return true;
			}
		} else if (holiday.type === "nth_weekday") {
			if (month === holiday.month && dayOfWeek === holiday.weekday) {
				if (holiday.occurrence === "last") {
					// Check if this is the last occurrence of this weekday in the month
					const nextWeek = new Date(date);
					nextWeek.setDate(day + 7);
					if (nextWeek.getMonth() + 1 !== month) {
						return true;
					}
				} else {
					// Check if it's the nth occurrence - match Python logic
					const weekOfMonth = Math.floor((day - 1) / 7) + 1;
					if (weekOfMonth === holiday.occurrence) {
						return true;
					}
				}
			}
		}
	}

	return false;
};

/**
 * Checks if a given date should be treated as a holiday for scheduling purposes
 * Equivalent to is_holiday.py - checks for day after holidays, observed holidays, etc.
 * @param {Date} date - The date to check
 * @param {Array} holidays - Array of holiday definitions from backend settings
 * @returns {boolean} - True if the date should be treated as a holiday for scheduling
 */
const isHolidayForScheduling = (date, holidays = []) => {
	const month = date.getMonth() + 1; // Convert JS 0-based to 1-based
	const day = date.getDate();
	const dayOfWeek = (date.getDay() + 6) % 7; // Convert to Python weekday numbering

	for (const holiday of holidays) {
		if (holiday.type === "day_after_fixed_holiday") {
			// Check if yesterday was the base holiday and today is a weekday
			const prevDay = new Date(date);
			prevDay.setDate(date.getDate() - 1);
			const prevMonth = prevDay.getMonth() + 1;
			const prevDayOfMonth = prevDay.getDate();

			if (prevMonth === holiday.base_holiday_month && prevDayOfMonth === holiday.base_holiday_day) {
				if (holiday.only_if_weekday) {
					// Only return True if today (day after the holiday) is a weekday
					if (dayOfWeek <= 4) { // Monday=0 through Friday=4
						return true;
					}
				} else {
					return true;
				}
			}
		} else if (holiday.type === "observed_fixed_holiday") {
			// Check if today is Monday and the base holiday fell on the weekend
			if (dayOfWeek === 0) { // Monday
				// Check if Saturday or Sunday was the base holiday
				const prevDay = new Date(date);
				prevDay.setDate(date.getDate() - 1); // Sunday
				const prevTwoDays = new Date(date);
				prevTwoDays.setDate(date.getDate() - 2); // Saturday

				const baseHoliday = [holiday.base_holiday_month, holiday.base_holiday_day];
				const prevDayMatch = [prevDay.getMonth() + 1, prevDay.getDate()];
				const prevTwoDaysMatch = [prevTwoDays.getMonth() + 1, prevTwoDays.getDate()];

				if ((prevDayMatch[0] === baseHoliday[0] && prevDayMatch[1] === baseHoliday[1]) ||
					(prevTwoDaysMatch[0] === baseHoliday[0] && prevTwoDaysMatch[1] === baseHoliday[1])) {
					return true;
				}
			}
		} else if (holiday.type === "day_after_nth_weekday") {
			// Check if yesterday was the base nth_weekday holiday (e.g., Black Friday = day after Thanksgiving)
			const prevDay = new Date(date);
			prevDay.setDate(date.getDate() - 1);
			const prevMonth = prevDay.getMonth() + 1;
			const prevDayOfMonth = prevDay.getDate();
			const prevDayOfWeek = (prevDay.getDay() + 6) % 7; // Convert to Python weekday numbering

			if (prevMonth === holiday.base_month && prevDayOfWeek === holiday.base_weekday) {
				// Check if yesterday was the correct occurrence
				if (holiday.base_occurrence === "last") {
					// Check if yesterday was the last occurrence of that weekday in the month
					const nextWeek = new Date(prevDay);
					nextWeek.setDate(prevDayOfMonth + 7);
					if (nextWeek.getMonth() + 1 !== prevMonth) {
						return true;
					}
				} else {
					// Check if yesterday was the nth occurrence
					const weekOfMonth = Math.floor((prevDayOfMonth - 1) / 7) + 1;
					if (weekOfMonth === holiday.base_occurrence) {
						return true;
					}
				}
			}
		} else if (holiday.type === "nth_weekday") {
			if (month === holiday.month && dayOfWeek === holiday.weekday) {
				if (holiday.occurrence === "last") {
					// Check if this is the last occurrence of this weekday in the month
					const nextWeek = new Date(date);
					nextWeek.setDate(day + 7);
					if (nextWeek.getMonth() + 1 !== month) {
						return true;
					}
				} else {
					// Check if it's the nth occurrence
					const weekOfMonth = Math.floor((day - 1) / 7) + 1;
					if (weekOfMonth === holiday.occurrence) {
						return true;
					}
				}
			}
		}
	}

	return false;
};

/**
 * Checks if the center is closed on a given date
 * @param {Date} date - The date to check
 * @param {Array} centerOpen - Array of boolean values for each day of the week
 * @param {Array} centerClosedDates - Array of holiday definitions from backend settings
 * @returns {boolean} - True if the center is closed, false if open
 */
export const isCenterClosed = (date, centerOpen, centerClosedDates = []) => {
	if (!centerOpen || centerOpen.length === 0) {
		return false; // If no schedule defined, assume open
	}

	const weekday = getWeekday(date);
	const isClosedBySchedule = !centerOpen[weekday];
	const isClosedByHoliday = isHoliday(date, centerClosedDates);

	return isClosedBySchedule || isClosedByHoliday;
};

/**
 * Finds the first open date starting from a given date
 * @param {Date} startDate - The date to start checking from
 * @param {Array} centerOpen - Array of boolean values for each day of the week
 * @param {Array} centerClosedDates - Array of holiday definitions from backend settings
 * @param {number} maxDaysToCheck - Maximum number of days to check ahead (default 14)
 * @returns {Date} - The first open date
 */
const findFirstOpenDate = (startDate, centerOpen, centerClosedDates = [], maxDaysToCheck = 14) => {
	const currentDate = new Date(startDate);
	
	for (let i = 0; i < maxDaysToCheck; i++) {
		if (!isCenterClosed(currentDate, centerOpen, centerClosedDates)) {
			return new Date(currentDate); // Return a copy to avoid mutation
		}
		currentDate.setDate(currentDate.getDate() + 1);
	}
	
	// If no open date found within the limit, return the original start date
	// This is a fallback to prevent infinite loops
	return new Date(startDate);
};

export const getFirstOpenDateToday = (centerOpen, test_environment, centerClosedDates = []) => {

	const today = getDateToday(test_environment);
	return findFirstOpenDate(today, centerOpen, centerClosedDates);
};

export const getFirstOpenDateTomorrow = (centerOpen, test_environment, centerClosedDates = []) => {

	const tomorrow = getDateTomorrow(test_environment);
	return findFirstOpenDate(tomorrow, centerOpen, centerClosedDates);
};

export const getDateFormatted = date => {
	if (!date) {
		return null
	}

	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(2, "0") // Months are zero-based
	const day = String(date.getDate()).padStart(2, "0")
	return `${year}-${month}-${day}`
}

export const humanFormattedDate = date => {
	if (!date) {
		return null
	}

	return date.toLocaleDateString("en-US", {
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric"
	})
}

export const formatDateForKPIDashboard = dateString => {
	const date = new Date(dateString + " GMT-0800") // Set to PST
	return date.toLocaleDateString("en-US", {
		month: "short", // Short month format like "Feb"
		day: "numeric", // Day of the month like "3"
		year: "numeric"
	})
}

const getOrdinalSuffix = day => {
	if (day > 3 && day < 21) return "th" // Special case for teens
	switch (day % 10) {
		case 1:
			return "st"
		case 2:
			return "nd"
		case 3:
			return "rd"
		default:
			return "th"
	}
}

export const formatDateWithDayOfWeekFutureHuddle = dateStr => {
	const date = new Date(dateStr + " GMT-0800")

	const day = date.getDate()
	const dayOfWeek = new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(date) // e.g., "Monday"
	const month = new Intl.DateTimeFormat("en-US", { month: "long" }).format(date) // e.g., "September"

	// Combine the formatted date with the ordinal suffix
	return `${month} ${day}${getOrdinalSuffix(day)} (${dayOfWeek})`
}

export const getWeekday = date => {
	if (!date) {
		return null
	}

	return (date.getDay() + 6) % 7
}

export const getDayOfWeek = dateStr => {
	const daysOfWeek = [
		"Monday",
		"Tuesday",
		"Wednesday",
		"Thursday",
		"Friday",
		"Saturday",
		"Sunday"
	]
	const scheduleDatePST = moment.tz(dateStr, "America/Los_Angeles")
	const dayOfWeek = (scheduleDatePST.day() + 6) % 7 // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
	return daysOfWeek[dayOfWeek] // Get the day of the week (0 for Sunday, 6 for Saturday)
}

export const getDayOfWeekNumber = dateStr => {
	const scheduleDatePST = moment.tz(dateStr, "America/Los_Angeles")
	return (scheduleDatePST.day() + 6) % 7
}

export const getDayOfWeekAndDate = dateStr => {
	const date = new Date(dateStr + "T00:00:00")
	return {
		day: date.toLocaleDateString(undefined, { weekday: "long" }),
		date: date.toLocaleDateString(undefined, { month: "long", day: "numeric" })
	}
}

export const convertMinutesTo12HHMM = minutes => {
	if (isNaN(minutes) || minutes == null) {
		return "Invalid Time"
	}
	const hours = Math.floor(minutes / 60)
	const mins = minutes % 60
	const formattedHours = String(hours % 12 === 0 ? 12 : hours % 12).padStart(2, "0")
	const amPm = hours >= 12 ? "PM" : "AM"
	const formattedMinutes = String(mins).padStart(2, "0")
	return `${formattedHours}:${formattedMinutes} ${amPm}`
}

export const convertMinutesTo12HHAMPMSingleDigit = minutes => {
	if (isNaN(minutes) || minutes == null) {
		return "Invalid Time"
	}
	const hours = Math.floor(minutes / 60)
	const amPm = hours >= 12 ? "PM" : "AM"
	// Use hours % 12 to convert to 12-hour format, but don't pad with 0
	// Also convert 0 to 12 for 12 AM
	const formattedHours = hours % 12 === 0 ? 12 : hours % 12
	return `${formattedHours} ${amPm}`
}


export const convertMinutesToMilitaryTime = minutes => {
	const hours = Math.floor(minutes / 60)
	const mins = minutes % 60
	const formattedHours = String(hours).padStart(2, "0") // Keep hours in military time
	const formattedMinutes = String(mins).padStart(2, "0")
	return `${formattedHours}:${formattedMinutes}`
}

/*
* takes in military time XX:XX and returns the time in 12 hour format with AM/PM	
*/
export const convertMilitaryTimeTo12HourFormat = militaryTime => {
	const [hours, minutes] = militaryTime.split(":").map(Number)
	const period = hours >= 12 ? "PM" : "AM"
	const adjustedHour = hours % 12 || 12 // Converts 0 or 12 to 12 for AM/PM
	return `${adjustedHour}:${minutes} ${period}`
}

/**
 * Converts a time string in HH:mm format to minutes since midnight
 * 
 * @param {string} time - Time string in HH:mm format (24-hour)
 * @returns {number} Minutes since midnight
 * 
 * @example
 * convertTimeToMinutes("09:30") // returns 570
 * convertTimeToMinutes("13:45") // returns 825
 */
export function convertTimeToMinutes(time) {
    // Split the time string into hours and minutes
    const [hours, minutes] = time.split(":").map(Number);
    // Convert the time to minutes after midnight
    return hours * 60 + minutes;
}

// Function to round time to the nearest 10 minutes
export const roundToNearestTenMinutes = date => {
	const minutes = date.getMinutes()
	const roundedMinutes = Math.ceil(minutes / 10) * 10
	date.setMinutes(roundedMinutes, 0, 0) // Set rounded minutes, clear seconds and milliseconds
	return date
}

export const timeStringToMinutes = timeString => {
	if (!timeString) {
		return null
	}

	const [hours, minutes] = timeString.split(":").map(Number)
	return hours * 60 + minutes
}

export const convertTo12HourFormat = hour => {
	const period = hour >= 12 ? "PM" : "AM"
	const adjustedHour = hour % 12 || 12 // Converts 0 or 12 to 12 for AM/PM
	return `${adjustedHour} ${period}`
}

// Get the current time rounded to the nearest 10 minutes
export const getCurrentRoundedTime = () => {
	const now = new Date()
	const roundedTime = roundToNearestTenMinutes(now)
	const hours = roundedTime.getHours().toString().padStart(2, "0")
	const minutes = roundedTime.getMinutes().toString().padStart(2, "0")
	return `${hours}:${minutes}`
}

export const convertMinutesToHoursAndMinutesString = minutes => {
	const hours = Math.floor(minutes / 60)
	const remainingMinutes = minutes % 60
	let result = ''
	if (hours > 0) {
		result += `${hours} hr${hours !== 1 ? 's' : ''}`
	}
	if (remainingMinutes > 0) {
		result += (result ? ' ' : '') + `${remainingMinutes} min${remainingMinutes !== 1 ? 's' : ''}`
	}
	return result || '0 min'
}

export const useScheduleState = (sandbox, isTomorrowMode) => {
	return useSelector(state => {
		if (sandbox) return state.sandbox
		if (isTomorrowMode) return state.tomorrow
		return state.schedule
	})
}

export const getTimePeriods = (openTime, closeTime) => {
    return [
        { name: `${convertMinutesTo12HHAMPMSingleDigit(openTime)}-10 AM`, start: openTime, end: 600 },
        { name: "10AM-2PM", start: 600, end: 840 },
        { name: "2PM-5PM", start: 840, end: 1020 },
        { name: `5PM-${convertMinutesTo12HHAMPMSingleDigit(closeTime)}`, start: 1020, end: closeTime }
    ]
}

/**
 * Get the open time in minutes since midnight for a given date
 * Uses holiday open times if the date is a holiday, otherwise uses regular open times
 * @param {Date} date - The date to get open time for
 * @param {Array} openTimeMinutes - Array of regular open times in minutes for each weekday
 * @param {Array} holidayOpenTimeMinutes - Array of holiday open times in minutes for each weekday
 * @param {Array} holidays - Array of holiday definitions from backend settings
 * @returns {number} - Open time in minutes since midnight
 */
export const getOpenTimeMinutesForDate = (date, openTimeMinutes = [], holidayOpenTimeMinutes = [], holidays = []) => {
	const weekdayIndex = (date.getDay() + 6) % 7; // Convert to Python weekday numbering (Monday=0)
	
	if (isHolidayForScheduling(date, holidays)) {
		return holidayOpenTimeMinutes[weekdayIndex] || 0;
	} else {
		return openTimeMinutes[weekdayIndex] || 0;
	}
};

/**
 * Get the close time in minutes since midnight for a given date
 * Uses holiday close times if the date is a holiday, otherwise uses regular close times
 * @param {Date} date - The date to get close time for
 * @param {Array} closeTimeMinutes - Array of regular close times in minutes for each weekday
 * @param {Array} holidayCloseTimeMinutes - Array of holiday close times in minutes for each weekday
 * @param {Array} holidays - Array of holiday definitions from backend settings
 * @returns {number} - Close time in minutes since midnight
 */
export const getCloseTimeMinutesForDate = (date, closeTimeMinutes = [], holidayCloseTimeMinutes = [], holidays = []) => {
	const weekdayIndex = (date.getDay() + 6) % 7; // Convert to Python weekday numbering (Monday=0)
	
	if (isHolidayForScheduling(date, holidays)) {
		return holidayCloseTimeMinutes[weekdayIndex] || 0;
	} else {
		return closeTimeMinutes[weekdayIndex] || 0;
	}
};

/**
 * Determine if a day ends too early for the 1 PM schedule to work.
 * Returns true if there are less than 5 hours between 1 PM and the close time.
 * Equivalent to day_ends_early() in Python backend.
 * 
 * @param {Date} date - The date to check
 * @param {Array} closeTimeMinutes - Array of regular close times in minutes for each weekday
 * @param {Array} holidayCloseTimeMinutes - Array of holiday close times in minutes for each weekday
 * @param {Array} holidays - Array of holiday definitions from backend settings
 * @returns {boolean} - True if day ends early (less than 5 hours after 1 PM), false otherwise
 */
export const dayEndsEarly = (date, closeTimeMinutes = [], holidayCloseTimeMinutes = [], holidays = []) => {
	const SCHEDULE_RELEASE_TIME_MINUTES = 13 * 60; // 1 PM in minutes since midnight (780)
	const MINIMUM_REVIEW_TIME_MINUTES = 5 * 60;    // 5 hours in minutes (300)
	
	const closeTime = getCloseTimeMinutesForDate(date, closeTimeMinutes, holidayCloseTimeMinutes, holidays);
	
	// Calculate hours available for review after 1 PM schedule release
	const availableReviewTime = closeTime - SCHEDULE_RELEASE_TIME_MINUTES;
	
	// Return true if less than 5 hours available for review
	return availableReviewTime < MINIMUM_REVIEW_TIME_MINUTES;
};

/**
 * Get the schedule lock time. Staffing for the next open day locks at 11 AM PT
 * on every day.
 *
 * @returns {number} - Lock time in minutes since midnight
 */
export const getScheduleLockTime = () => {
	return 11 * 60; // 11 AM
};

/**
 * Convert time arrays from string format to minutes format
 * @param {Array} timeStrings - Array of time strings in HH:MM format
 * @returns {Array} - Array of times in minutes since midnight
 */
export function convertTimeArrayToMinutes(timeStrings = []) {
    return timeStrings.map(timeStr => convertTimeToMinutes(timeStr));
}

/**
 * returns the start and end time of the clinic in minutes since midnight for a specific date
 * 
 * @param {Object} settings - Center settings object
 * @param {Date} date - The date to get clinic times for
 * @returns {Object} Object containing openTime and closeTime in minutes since midnight
 */
export function getClinicTimes(settings, date) {
    // Convert time data to arrays, then to minutes if they're not already
	if (!settings) {
		return { openTime: 480, closeTime: 1200 }
	}
    const openTimeArray = settings.openTime || settings.open_time || [];
    const closeTimeArray = settings.closeTime || settings.close_time || [];
    const holidayOpenTimeArray = settings.holidayOpenTime || [];
    const holidayCloseTimeArray = settings.holidayCloseTime || [];
    
    const openTimeMinutes = settings.open_time_minutes || convertTimeArrayToMinutes(openTimeArray);
    const closeTimeMinutes = settings.close_time_minutes || convertTimeArrayToMinutes(closeTimeArray);
    const holidayOpenTimeMinutes = settings.holiday_open_time_minutes || convertTimeArrayToMinutes(holidayOpenTimeArray);
    const holidayCloseTimeMinutes = settings.holiday_close_time_minutes || convertTimeArrayToMinutes(holidayCloseTimeArray);

    const openTime = getOpenTimeMinutesForDate(
        date, 
        openTimeMinutes, 
        holidayOpenTimeMinutes, 
        settings.holidays || []
    );
    const closeTime = getCloseTimeMinutesForDate(
        date, 
        closeTimeMinutes, 
        holidayCloseTimeMinutes, 
        settings.holidays || []
    );
    return { openTime, closeTime };
}

/**
 * Gets the hours in a day based on the center's settings for a specific date
 * 
 * @param {Object} settings - Center settings object
 * @param {Date} date - The date to get hours for
 * @returns {number[]} Array of hours in minutes since midnight
 */
export function getHoursInDay(settings, date) {
    const { openTime, closeTime } = getClinicTimes(settings, date);
    
    // Round down startTime to the nearest hour (60-minute interval)
    const roundedStartTime = Math.floor(openTime / 60) * 60;
    
    const hours = [];
    
    for (let hour = roundedStartTime; hour < closeTime; hour += 60) {
        hours.push(hour);
    }
    
    return hours;
}



/**
 * Aligns a time to the 10 minute alignment
 *
 * alignTimeTo10Minutes(10) = 10
 * alignTimeTo10Minutes(11) = 10
 * alignTimeTo10Minutes(12) = 10
 * ...
 * alignTimeTo10Minutes(19) = 10
 * alignTimeTo10Minutes(20) = 20
 * alignTimeTo10Minutes(21) = 20
 * ...
 *
 * @param {number} time - The time to align
 * @returns {number} The aligned time
 */

export const alignTimeTo10Minutes = time => {
	return Math.round(time / 10) * 10
}

// Convert hour numbers to formatted time
export const formatTimeRange = (range) => {
	const [start, end] = range.split('-').map(Number);
	const formatHour = (h) => {
	  const suffix = h >= 12 ? 'PM' : 'AM';
	  return `${h > 12 ? h - 12 : h}${suffix}`;
	};
	return `${formatHour(start)}-${formatHour(end)}`;
  };
